// ConfirmRdvPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Spinner, Alert, Button, Container, Card } from 'react-bootstrap';

const API_BASE = 'https://1irywxa5c3.execute-api.ca-central-1.amazonaws.com/prod';

const isUUID = (v) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || ''));

export default function ConfirmRdvPage() {
  const { id } = useParams(); // appointment_id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null); // réponse du backend (message, telemedicine, etc.)

  const invalidReason = useMemo(() => {
    if (!id || id === 'undefined') return 'Lien invalide : identifiant manquant.';
    if (!isUUID(id)) return 'Lien invalide : identifiant de rendez-vous non valide.';
    return null;
  }, [id]);

  useEffect(() => {
    const run = async () => {
      if (invalidReason) {
        setError(invalidReason);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const { data } = await axios.put(`${API_BASE}/appointments/${id}/confirm`);
        setPayload(data || {});
        setConfirmed(true);
      } catch (err) {
        // Rendre l’erreur plus lisible
        const status = err?.response?.status;
        const msg =
          err?.response?.data?.error ||
          (status === 400 && "Requête invalide. Vérifiez l'URL du lien.") ||
          (status === 404 && 'Rendez-vous introuvable ou déjà supprimé.') ||
          'Une erreur s’est produite lors de la confirmation du rendez-vous.';
        setError(msg);
        console.error('[ConfirmRdvPage] PUT failed:', err);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id, invalidReason]);

  const telemed = payload?.telemedicine;
  const hasTelemed = !!(telemed?.patientJoinUrl || telemed?.professionalJoinUrl);

  return (
    <Container className="my-5">
      <Card className="shadow p-4 text-center">
        {loading && (
          <>
            <Spinner animation="border" role="status" />
            <div className="mt-3">Confirmation en cours…</div>
          </>
        )}

        {!loading && !!error && (
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && confirmed && !error && (
          <>
            <h3 className="text-success mb-3">✅ Rendez-vous confirmé</h3>

            {payload?.message && <p className="mb-2">{payload.message}</p>}

            {payload?.appointment_id && (
              <p className="text-muted mb-3">
                ID du rendez-vous&nbsp;: <code>{payload.appointment_id}</code>
              </p>
            )}

            {hasTelemed && (
              <div className="mb-4">
                <h5 className="mb-3">Téléconsultation</h5>
                {telemed?.professionalJoinUrl && (
                  <div className="mb-2">
                    <a
                      className="btn btn-primary"
                      href={telemed.professionalJoinUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Ouvrir la téléconsultation (professionnel)
                    </a>
                  </div>
                )}
                {telemed?.patientJoinUrl && (
                  <div className="mb-2">
                    <a
                      className="btn btn-outline-primary"
                      href={telemed.patientJoinUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Lien patient (aperçu)
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="d-flex justify-content-center gap-2">
              <Button variant="primary" onClick={() => navigate('/professional/calendrier')}>
                Voir mon calendrier
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                Retour
              </Button>
            </div>
          </>
        )}
      </Card>
    </Container>
  );
}